import { rm } from "node:fs/promises";
import { join } from "node:path";
import { track } from "../../../core/analytics";
import type { AgentLauncher, PermissionMode } from "../../../core/coding-agent";
import { selectLauncher } from "../../../core/coding-agent";
import { debugLog } from "../../../core/debug";
import { suspend, resume } from "../../../core/interrupt";
import { notify } from "../../../core/notify";
import { resolvePermissionMode } from "../../../core/permission-mode";
import * as p from "../../../ui/prompts";
import { COMPLETION_MARKER_FILE, readCompletion } from "../completion";
import { watchForCompletion } from "../completion-watch";
import { INTEGRATION_PROMPT_FILE, writeIntegrationPrompt } from "../integration-prompt";
import { type AgentLaunch, describeLaunchOutcome } from "../launch-outcome";
import { findRecipeUploadProblems, loadRecipe, type RecipeReadResult, RECIPE_FILE } from "../recipe";
import type { RecipeBuilderState } from "../state";
import { saveRecipeState } from "../state";

/** Total interactive launches allowed: the first, plus one re-launch to finish. */
export const MAX_LAUNCH_ATTEMPTS = 2;

/** Breathing room before the terminal switches to the coding agent. */
const HANDOFF_COUNTDOWN_SECONDS = 10;

/**
 * Why the integration never ran. Both the handoff and the completion phase can hit
 * this - completion reaches it on a `--resume` into a run that paused here - and they
 * must say the same thing, because the recipe being absent is the symptom, not the cause.
 */
const NO_AGENT_FOUND = "Autonoma can launch Claude Code or Codex for you, but neither is on your PATH.";
const NO_AGENT_FOUND_HEADLESS =
    "--non-interactive SDK implementation requires an installed, supported coding agent, but none " +
    "was resolved on your PATH (name one with --agent if more than one is present). Install one, " +
    "or run interactively.";

/** Points the agent at the rendered prompt file it must read and follow. */
function launchMessage(promptFile: string): string {
    return (
        `Read the file ${promptFile} and follow its instructions exactly to integrate ` +
        `Autonoma into this application. It is your complete spec. Do not stop until every ` +
        `item in it is done and you have written the completion marker it describes.`
    );
}

export interface HandoffDeps {
    /** Launchers to detect/select over. Injected in tests; built from the repo in prod. */
    launchers: AgentLauncher[];
    /** Preset agent id from `--agent`. */
    presetAgentId?: string;
    /** Preset permission mode from `--permission-mode`. */
    presetPermissionMode?: PermissionMode;
    /** How the agent invokes the CLI's endpoint tool: `<cliCommand> sdk up|down|discover`. */
    cliCommand: string;
    /** Interactive run: prompt + attach the agent to the TTY. Otherwise run headless,
     *  autonomous, no prompts, and hard-error (not manual-fallback) when no agent exists. */
    interactive: boolean;
}

/** What a phase run tells the orchestrator to do next. */
export type PhaseOutcome =
    | { kind: "advance" }
    | { kind: "pause"; summary: string }
    | { kind: "handback"; summary: string };

/**
 * Hand the whole integration to the developer's own coding agent (e.g. Claude Code).
 * When an agent is available it just launches - no "do you want to?" prompt; the
 * whole point of this step is that the agent implements the integration. Interactive
 * runs pick an autonomy level and attach it to the terminal. Only a genuinely missing
 * agent drops to the manual fallback (a note, not a question): interactively it prints
 * the instructions to run by hand; headless it hard-errors.
 */
export async function runHandoffPhase(
    state: RecipeBuilderState,
    deps: HandoffDeps,
    outputDir: string,
): Promise<PhaseOutcome> {
    const recipePath = state.recipePath ?? join(outputDir, RECIPE_FILE);
    state.recipePath = recipePath;

    const launcher = await selectLauncher(deps.launchers, state.agentId ?? deps.presetAgentId, deps.interactive);
    if (launcher == null) {
        // Non-interactive requires an agent - there is no manual fallback without a TTY.
        if (!deps.interactive) {
            return { kind: "handback", summary: NO_AGENT_FOUND_HEADLESS };
        }
        return manualFallback(outputDir, recipePath, deps.cliCommand, NO_AGENT_FOUND);
    }

    const permissionMode = await resolvePermissionMode({
        preset: deps.presetPermissionMode,
        remembered: state.permissionMode,
        interactive: deps.interactive,
    });
    state.agentId = launcher.id;
    state.permissionMode = permissionMode;
    await saveRecipeState(outputDir, state);

    state.lastLaunch = await launchAgent(launcher, permissionMode, {
        outputDir,
        recipePath,
        cliCommand: deps.cliCommand,
        interactive: deps.interactive,
        priorFailure: state.priorFailure,
        userGuidance: state.userGuidance,
    });
    state.launchAttempts = (state.launchAttempts ?? 0) + 1;
    await saveRecipeState(outputDir, state);

    // launchAgent awaited the coding agent's exit, so control is back here; the
    // completion phase (next) decides success from the marker + recipe, not the exit code.
    return { kind: "advance" };
}

/**
 * CLI-side orchestration - the coding agent is a separate, already-exited process and
 * never runs this code. "Done" is purely file-based: the completion marker the agent
 * wrote plus a present recipe.json (the agent owns all validation - per entity: up ->
 * DB -> down -> DB). We NEVER ask the user whether they implemented it.
 *
 * If it isn't done and a supported agent is available, (re-)launch it to finish, up to
 * MAX_LAUNCH_ATTEMPTS. This also self-heals a run left mid-handoff - e.g. a prior
 * session whose state advanced to this phase without an agent ever launching. When no
 * agent is available or the attempts are spent, control hands back with instructions.
 */
export async function runCompletionPhase(
    state: RecipeBuilderState,
    deps: HandoffDeps,
    outputDir: string,
): Promise<PhaseOutcome> {
    const recipePath = state.recipePath ?? join(outputDir, RECIPE_FILE);

    while (true) {
        const [complete, read] = await Promise.all([readCompletion(outputDir), loadRecipe(outputDir)]);
        // A recipe that parses can still be unsubmittable - a dangling `_ref`, a token
        // that resolves to nothing - so treat that as not-ready and hand the exact
        // problems back for the agent to fix, rather than uploading into a rejection.
        const uploadProblems = read.status === "ok" ? findRecipeUploadProblems(read.recipe) : [];
        const recipeReady = read.status === "ok" && uploadProblems.length === 0;

        if (complete && recipeReady) {
            p.log.success("The agent reported the integration complete and validated.");
            return { kind: "advance" };
        }

        const launcher = await selectLauncher(deps.launchers, state.agentId ?? deps.presetAgentId, deps.interactive);

        // Nothing was ever produced and nothing could have produced it: no agent is
        // installed and none has run. The absent recipe is the symptom - reporting it
        // sends the developer looking for a file, when what they need is the missing
        // agent and the manual instructions. A recipe that exists (even a broken one)
        // means something did run, so those keep their own diagnosis below.
        const nothingEverRan = read.status === "absent" && launcher == null && (state.launchAttempts ?? 0) === 0;
        if (nothingEverRan) {
            if (!deps.interactive) return { kind: "handback", summary: NO_AGENT_FOUND_HEADLESS };
            return await manualFallback(outputDir, recipePath, deps.cliCommand, NO_AGENT_FOUND);
        }

        const failure = describeIncompleteRecipe(read, uploadProblems, recipePath, deps.cliCommand);
        const retry = await planRetry(state, launcher, deps, failure);
        if (retry.kind === "give-up") return { kind: "handback", summary: handbackSummary(retry.summary, outputDir) };

        p.log.warn(retry.announcement);
        state.agentId = retry.launcher.id;
        state.priorFailure = retry.priorFailure;
        await saveRecipeState(outputDir, state);

        state.lastLaunch = await launchAgent(retry.launcher, state.permissionMode ?? "bypassPermissions", {
            outputDir,
            recipePath,
            cliCommand: deps.cliCommand,
            interactive: deps.interactive,
            priorFailure: retry.priorFailure,
            userGuidance: state.userGuidance,
        });
        state.launchAttempts = (state.launchAttempts ?? 0) + 1;
        await saveRecipeState(outputDir, state);
    }
}

/** What the completion phase does next when the integration is not finished. */
type RetryPlan =
    | { kind: "relaunch"; launcher: AgentLauncher; priorFailure?: string; announcement: string }
    | { kind: "give-up"; summary: string };

/**
 * Whether relaunching can plausibly finish the integration, and with which agent.
 *
 * The distinction that decides this is whether the last launch RAN. A session that
 * worked and fell short is exactly what the re-launch exists for, and it gets the
 * recipe diagnosis so it resumes rather than starting over. A session that exited
 * immediately did no work at all, and running the same binary again does not do more
 * - on real machines both allowed attempts were spent that way in under a second,
 * after which the user was told a recipe file was missing. So a failed-to-start
 * launch moves to a different agent if one is installed, and otherwise stops and
 * says what actually went wrong. The attempt ceiling still applies to both paths,
 * so two agents that each fail to start cannot hand the run back and forth.
 */
async function planRetry(
    state: RecipeBuilderState,
    launcher: AgentLauncher | undefined,
    deps: HandoffDeps,
    failure: string,
): Promise<RetryPlan> {
    const lastLaunch = state.lastLaunch;
    const outcome = lastLaunch == null ? undefined : describeLaunchOutcome(lastLaunch);
    const failedToStart = outcome?.kind === "failed-to-start" ? outcome.summary : undefined;
    // A launch that never ran says nothing about the integration, so reporting the
    // absent recipe would tell the wrong story - that is the swap this exists for.
    const summary = failedToStart ?? failure;

    if (launcher == null) return { kind: "give-up", summary };
    if ((state.launchAttempts ?? 0) >= MAX_LAUNCH_ATTEMPTS) return { kind: "give-up", summary };

    if (failedToStart != null && lastLaunch != null) {
        const alternative = await findOtherAvailableAgent(deps.launchers, lastLaunch.agentId);
        if (alternative == null) return { kind: "give-up", summary: failedToStart };
        return {
            kind: "relaunch",
            launcher: alternative,
            // No prior failure: nothing was attempted, so telling a fresh agent that a
            // prior session "did not complete" would send it hunting for work that was
            // never started.
            announcement: `${failedToStart} Trying ${alternative.label} instead...`,
        };
    }

    return {
        kind: "relaunch",
        launcher,
        priorFailure: failure,
        announcement: "The integration isn't complete yet. Launching the agent to finish it...",
    };
}

/**
 * An installed agent that is not the one that just failed to start. Availability is
 * re-probed rather than remembered: this runs precisely because something about the
 * chosen agent turned out not to work.
 */
async function findOtherAvailableAgent(
    launchers: AgentLauncher[],
    failedAgentId: string,
): Promise<AgentLauncher | undefined> {
    const others = launchers.filter((l) => l.id !== failedAgentId);
    const availability = await Promise.all(others.map((l) => l.isAvailable()));
    return others.find((_, index) => availability[index]);
}

function handbackSummary(failure: string, outputDir: string): string {
    return (
        `${failure}\n\n` +
        `Finish the integration (see the prompt at ${join(outputDir, INTEGRATION_PROMPT_FILE)}), ` +
        `then re-run with --resume.`
    );
}

interface LaunchTarget {
    outputDir: string;
    recipePath: string;
    cliCommand: string;
    interactive: boolean;
    priorFailure?: string;
    userGuidance?: string;
}

/**
 * Render the prompt file and run the agent with the terminal handed over to it,
 * reporting what the launch did.
 *
 * The exit code does not decide whether the integration succeeded - the marker and
 * the recipe do - but it is the only evidence of whether the agent ran at all, and
 * discarding it is what let a session that died in 300ms be treated the same as one
 * that worked for half an hour.
 */
async function launchAgent(
    launcher: AgentLauncher,
    permissionMode: PermissionMode,
    target: LaunchTarget,
): Promise<AgentLaunch> {
    const promptFile = await writeIntegrationPrompt({
        outputDir: target.outputDir,
        recipePath: target.recipePath,
        cliCommand: target.cliCommand,
        priorFailure: target.priorFailure,
        userGuidance: target.userGuidance,
    });

    // A stale marker from an earlier session would make the completion watcher
    // reclaim the terminal seconds after launch - the agent must write it fresh.
    await rm(join(target.outputDir, COMPLETION_MARKER_FILE), { force: true }).catch((err) => {
        debugLog("Could not clear a stale completion marker", { err });
    });

    p.log.info(`Launching ${launcher.label}. It will implement the integration - watch and steer it as it works.`);
    notify("Autonoma", "Handing off to your local agent");

    if (target.interactive) {
        await p.countdown({
            title: `Handing off to ${launcher.label}`,
            lines: [
                `Your terminal is about to switch to ${launcher.label}. It will implement the ` +
                    `Autonoma SDK integration inside your repo: install the SDK, wire the endpoint, ` +
                    `and write a real factory for every entity in the audit, validating each one ` +
                    `against your locally running app. It works on its own branch and opens a pull ` +
                    `request with the result.`,
                `This dashboard disappears while it works - that's expected. Watch and steer it ` +
                    `like any ${launcher.label} session; this usually takes a while.`,
                `When it finishes and exits, you come straight back here and the planner continues ` +
                    `where it left off: submitting the validated recipe, then generating your test suite.`,
            ],
            seconds: HANDOFF_COUNTDOWN_SECONDS,
        });
    }

    // Hand the terminal (SIGINT + raw mode) to the agent, then take it back.
    suspend();
    const startedAt = Date.now();
    try {
        const exitCode = await launcher.launch({
            message: launchMessage(promptFile),
            permissionMode,
            interactive: target.interactive,
            // An interactive session sits open after its final message, so the marker
            // the agent writes is the real "done" signal. A headless run exits on its
            // own and needs no watcher.
            watch: target.interactive ? (proc) => watchForCompletion(target.outputDir, proc) : undefined,
        });
        const launch: AgentLaunch = {
            agentLabel: launcher.label,
            agentId: launcher.id,
            exitCode,
            durationMs: Date.now() - startedAt,
        };
        p.log.info(
            `${launcher.label} exited (code ${exitCode ?? "unknown"}) after ` +
                `${Math.round(launch.durationMs / 1000)}s. Back to the planner.`,
        );
        track("cli_agent_launch_finished", {
            agent: launcher.id,
            exit_code: exitCode,
            duration_ms: launch.durationMs,
            outcome: describeLaunchOutcome(launch).kind,
        });
        return launch;
    } finally {
        resume();
    }
}

/**
 * Why the recipe isn't submittable yet, phrased for the agent that has to fix it. Every
 * recipe-shaped failure names the file to edit and the command that re-checks it, so the
 * relaunched agent has a closed fix-verify loop instead of guessing what "invalid" meant.
 */
function describeIncompleteRecipe(
    read: RecipeReadResult,
    uploadProblems: string[],
    recipePath: string,
    cliCommand: string,
): string {
    const recheck = `Fix ${recipePath}, then re-check it with:\n  ${cliCommand} sdk check --recipe ${recipePath}`;

    if (read.status === "absent") {
        return `No completed recipe.json was produced, so there is nothing validated to submit. It belongs at ${recipePath}.`;
    }
    if (read.status === "invalid") {
        return (
            `${recipePath} does not match the recipe format Autonoma accepts:\n` +
            read.problems.map((problem) => `  - ${problem}`).join("\n") +
            `\n${recheck}`
        );
    }
    if (uploadProblems.length > 0) {
        return (
            `${RECIPE_FILE} will be rejected on upload:\n` +
            uploadProblems.map((problem) => `  - ${problem}`).join("\n") +
            `\n${recheck}`
        );
    }
    return "The agent exited without marking the integration complete (see its IMPLEMENTATION.md for what's left).";
}

/**
 * Manual fallback: no automated run. Point the developer at the rendered prompt so
 * they can implement it in whatever assistant they have, then pause so a later
 * `--resume` confirms completion and continues to test generation.
 */
async function manualFallback(
    outputDir: string,
    recipePath: string,
    cliCommand: string,
    why: string,
): Promise<PhaseOutcome> {
    const promptFile = await writeIntegrationPrompt({ outputDir, recipePath, cliCommand });
    // A modal, not a note: this is the one step the CLI cannot do for itself, and a
    // line scrolling past in the activity feed is how people end up stuck believing
    // the run failed. It blocks until acknowledged.
    await p.welcome({
        eyebrow: "NO CODING AGENT FOUND",
        title: "Finish this step in the coding agent you already use",
        lines: [
            why,
            "Any coding agent can do this step just as well - Cursor, Windsurf, Copilot, Zed, " +
                "or whatever you have open. Point yours at this project and paste it this:",
            `Read ${promptFile} and follow its instructions exactly.`,
            "That file is the complete spec: the endpoint to implement, the factories to " +
                "register, and the completion marker it must write when it is done.",
            `When your agent reports it finished, come back here and run:  ${cliCommand} --resume`,
        ],
        cta: "Press enter once you have handed it over",
    });
    return {
        kind: "pause",
        summary: `${why} Integration handed off for manual implementation - resume when it's ready.`,
    };
}
